import React, {useState, useMemo} from 'react';
import {NavLink, useLocation} from 'react-router-dom';
import type {NavigationItem} from './navigationConfig';
import NavigationIcon from './NavigationIcon';
import {filterNavigationItems} from '../../utils/navigationUtils';
import {useAuthorization} from '../../../features/auth/hooks/useAuthorization';
import {useClientTheme, useClientProfile} from '../../../core/context/ClientContext';
import {getNavItemsForModules} from '../../../core/features/registry';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({isCollapsed, onToggle}) => {
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const location = useLocation();
  const {user} = useAuthorization();
  const theme = useClientTheme();
  const profile = useClientProfile();

  const visibleNavigationItems = useMemo(() => {
    const navItems = getNavItemsForModules(profile.enabledModules);
    return filterNavigationItems(navItems as NavigationItem[], user);
  }, [profile.enabledModules, user]);

  const toggleGroup = (groupLabel: string) => {
    setExpandedGroups(prev =>
      prev.includes(groupLabel)
        ? prev.filter(label => label !== groupLabel)
        : [...prev, groupLabel]
    );
  };

  const renderNavItem = (item: NavigationItem, level: number = 0) => {
    const isGroup = item.isGroup && item.children && item.children.length > 0;
    const isExpanded = expandedGroups.includes(item.label);
    const hasActiveChild = item.children?.some(child => location.pathname === child.path);

    if (isGroup) {
      return (
        <div key={item.label} className="w-full">
          <button
            onClick={() => toggleGroup(item.label)}
            style={{
              color: hasActiveChild
                ? 'var(--color-sidebar-text-active, #1d4ed8)'
                : 'var(--color-sidebar-text, #374151)',
              background: hasActiveChild
                ? 'var(--color-sidebar-bg-active, #eff6ff)'
                : 'transparent',
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 text-right font-medium text-base transition-all duration-200 rounded-xl hover:opacity-90 ${
              level > 0 ? 'mr-4' : ''
            }`}
            onMouseEnter={e => {
              if (!hasActiveChild)
                (e.currentTarget as HTMLElement).style.background =
                  'var(--color-sidebar-bg-hover, #f3f4f6)';
            }}
            onMouseLeave={e => {
              if (!hasActiveChild)
                (e.currentTarget as HTMLElement).style.background = 'transparent';
            }}
          >
            <span style={{color: hasActiveChild ? 'var(--color-sidebar-icon-active, #1d4ed8)' : 'var(--color-sidebar-icon, #9ca3af)'}}>
              {item.icon ? <NavigationIcon icon={item.icon} /> : <div className="w-6 h-6 rounded flex items-center justify-center text-xs">?</div>}
            </span>
            {!isCollapsed && (
              <>
                <span className="flex-1 text-right">{item.label}</span>
                <svg
                  className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                  style={{color: 'var(--color-sidebar-text-muted, #9ca3af)'}}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 10.168l3.71-2.938a.75.75 0 1 1 .94 1.176l-4.25 3.37a.75.75 0 0 1-.94 0l-4.25-3.37a.75.75 0 0 1 .02-1.06Z" clipRule="evenodd" />
                </svg>
              </>
            )}
            {isCollapsed && <span className="sr-only">{item.label}</span>}
          </button>

          {isExpanded && !isCollapsed && (
            <div className="mt-1 space-y-1">
              {item.children?.map(child => renderNavItem(child, level + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <NavLink
        key={item.path}
        to={item.path || '#'}
        end
        className={`w-full flex items-center gap-3 px-4 py-3 text-right font-medium text-base transition-all duration-200 rounded-xl ${
          level > 0 ? 'mr-4' : ''
        }`}
        style={({isActive}) => ({
          color: isActive
            ? 'var(--color-sidebar-text-active, #1d4ed8)'
            : 'var(--color-sidebar-text, #374151)',
          background: isActive
            ? 'var(--color-sidebar-bg-active, #eff6ff)'
            : 'transparent',
          borderRight: isActive
            ? '3px solid var(--color-sidebar-text-active, #1d4ed8)'
            : '3px solid transparent',
        })}
        onMouseEnter={e => {
          const el = e.currentTarget as HTMLElement;
          if (!el.dataset.active)
            el.style.background = 'var(--color-sidebar-bg-hover, #f3f4f6)';
        }}
        onMouseLeave={e => {
          const el = e.currentTarget as HTMLElement;
          if (!el.dataset.active)
            el.style.background = 'transparent';
        }}
      >
        {({isActive}) => (
          <>
            <span style={{color: isActive ? 'var(--color-sidebar-icon-active, #1d4ed8)' : 'var(--color-sidebar-icon, #9ca3af)'}}>
              {item.icon ? <NavigationIcon icon={item.icon} /> : <div className="w-6 h-6 rounded flex items-center justify-center text-xs">?</div>}
            </span>
            {!isCollapsed && (
              <>
                <span className="flex-1 text-right">{item.label}</span>
                {item.badge && (
                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{item.badge}</span>
                )}
              </>
            )}
            {isCollapsed && <span className="sr-only">{item.label}</span>}
          </>
        )}
      </NavLink>
    );
  };

  return (
    <aside
      style={{
        background: 'var(--color-sidebar-bg, #ffffff)',
        borderColor: 'var(--color-sidebar-border, #e5e7eb)',
      }}
      className={`shadow-xl transition-all duration-300 ease-in-out ${
        isCollapsed ? 'w-16' : 'w-72'
      } h-full flex flex-col border-r`}
      dir="rtl"
    >
      {/* Header */}
      <div
        style={{
          borderColor: 'var(--color-sidebar-border, #e5e7eb)',
          background: 'var(--color-sidebar-logo-bg, var(--color-sidebar-bg, #ffffff))',
        }}
        className="flex-shrink-0 p-4 border-b"
      >
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <div className="flex items-center gap-3">
              {theme.logoUrl ? (
                <img
                  src={theme.logoUrl}
                  alt={theme.brandName ?? 'Logo'}
                  className="h-10 w-auto object-contain max-w-[160px]"
                  style={{filter: 'var(--logo-sidebar-filter, none)'}}
                />
              ) : (
                <div
                  style={{background: 'var(--color-primary, #1d4ed8)'}}
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                >
                  <span className="text-white font-bold text-sm">
                    {(theme.brandName ?? 'A').charAt(0)}
                  </span>
                </div>
              )}
              {!theme.logoUrl && (
                <span
                  style={{color: 'var(--color-sidebar-text, #111827)'}}
                  className="font-bold text-lg"
                >
                  {theme.brandName ?? 'Dashboard'}
                </span>
              )}
            </div>
          )}

          {/* Toggle Button */}
          <button
            onClick={onToggle}
            style={{
              background: 'var(--color-sidebar-bg-hover, #f3f4f6)',
              color: 'var(--color-sidebar-text, #374151)',
            }}
            className="w-10 h-10 flex items-center justify-center rounded-xl transition-colors duration-200 focus:outline-none focus:ring-2"
            aria-label={isCollapsed ? 'توسيع الشريط الجانبي' : 'تصغير الشريط الجانبي'}
          >
            <svg
              className={`w-5 h-5 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav id="sidebar-navigation" className="flex-1 p-3 space-y-1 overflow-y-auto">
        {visibleNavigationItems.length > 0 ? (
          visibleNavigationItems.map(item => renderNavItem(item))
        ) : (
          <div
            style={{color: 'var(--color-sidebar-text-muted, #9ca3af)'}}
            className="text-center py-4 text-sm"
          >
            {user ? 'لا توجد عناصر تنقل متاحة' : 'جاري التحميل...'}
          </div>
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
